import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const FilterPanel = ({ 
  isOpen, 
  onClose, 
  onFilterChange, 
  initialFilters = {} 
}) => {
  const [filters, setFilters] = useState({
    query: '',
    location: '',
    productType: '',
    minPrice: '',
    maxPrice: '',
    organic: '',
    quantity: '',
    season: '',
    ...initialFilters
  });

  useEffect(() => {
    setFilters(prev => ({ ...prev, ...initialFilters }));
  }, [initialFilters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      query: '',
      location: '',
      productType: '',
      minPrice: '',
      maxPrice: '',
      organic: '',
      quantity: '',
      season: ''
    };
    setFilters(clearedFilters);
    if (onFilterChange) {
      onFilterChange(clearedFilters);
    }
  };

  const productTypes = [
    { value: '', label: 'All Products' },
    { value: 'vegetables', label: 'Vegetables' },
    { value: 'fruits', label: 'Fruits' },
    { value: 'grains', label: 'Grains' },
    { value: 'dairy', label: 'Dairy' },
    { value: 'meat', label: 'Meat & Poultry' },
    { value: 'herbs', label: 'Herbs & Spices' }
  ];

  const priceRanges = [
    { value: '', label: 'Any Price' },
    { value: '0-25', label: 'Under $25' },
    { value: '25-50', label: '$25 - $50' },
    { value: '50-100', label: '$50 - $100' },
    { value: '100-200', label: '$100 - $200' },
    { value: '200-500', label: '$200 - $500' },
    { value: '500+', label: 'Over $500' }
  ];

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile Overlay */}
      <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={onClose}></div>
      
      {/* Filter Panel */}
      <div className={`
        fixed lg:static inset-y-0 left-0 w-80 bg-surface border-r border-border z-50
        transform transition-transform duration-300 ease-out lg:transform-none
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        overflow-y-auto
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-background">
          <h2 className="text-lg font-semibold text-text-primary">Filters</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={clearAllFilters}
              className="text-sm text-text-secondary hover:text-text-primary transition-colors duration-200"
            >
              Clear All
            </button>
            <button
              onClick={onClose}
              className="lg:hidden p-1 hover:bg-secondary-100 rounded-md transition-colors duration-200"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {/* Filter Content */}
        <div className="p-4 space-y-6">
          {/* Search Query */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Search Products
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon name="Search" size={16} className="text-text-secondary" />
              </div>
              <input
                type="text"
                value={filters.query}
                onChange={(e) => handleFilterChange('query', e.target.value)}
                placeholder="Search farm products..."
                className="block w-full pl-9 pr-3 py-2 border border-border rounded-md text-sm
                         focus:border-border-focus focus:ring-1 focus:ring-primary-500 
                         transition-all duration-200 ease-out bg-surface text-text-primary
                         placeholder-text-secondary"
              />
            </div>
          </div>

          {/* Farm Location */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Farm Location
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon name="MapPin" size={16} className="text-text-secondary" />
              </div>
              <input
                type="text"
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                placeholder="Enter state or location..."
                className="block w-full pl-9 pr-3 py-2 border border-border rounded-md text-sm
                         focus:border-border-focus focus:ring-1 focus:ring-primary-500 
                         transition-all duration-200 ease-out"
              />
            </div>
          </div>

          {/* Product Type */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Product Type
            </label>
            <select
              value={filters.productType}
              onChange={(e) => handleFilterChange('productType', e.target.value)}
              className="block w-full px-3 py-2 border border-border rounded-md text-sm
                       focus:border-border-focus focus:ring-1 focus:ring-primary-500 
                       transition-all duration-200 ease-out"
            >
              {productTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Price Range */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Price Range
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                value={filters.minPrice}
                onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                placeholder="Min ($)"
                className="block w-full px-3 py-2 border border-border rounded-md text-sm
                         focus:border-border-focus focus:ring-1 focus:ring-primary-500 
                         transition-all duration-200 ease-out"
              />
              <input
                type="number"
                value={filters.maxPrice}
                onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                placeholder="Max ($)"
                className="block w-full px-3 py-2 border border-border rounded-md text-sm
                         focus:border-border-focus focus:ring-1 focus:ring-primary-500 
                         transition-all duration-200 ease-out"
              />
            </div>
          </div>

          {/* Organic Certification */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Organic Certified
            </label>
            <select
              value={filters.organic}
              onChange={(e) => handleFilterChange('organic', e.target.value)}
              className="block w-full px-3 py-2 border border-border rounded-md text-sm
                       focus:border-border-focus focus:ring-1 focus:ring-primary-500 
                       transition-all duration-200 ease-out"
            >
              <option value="">Any</option>
              <option value="yes">Organic Only</option>
              <option value="no">Conventional</option>
            </select>
          </div>

          {/* Quantity Available */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Quantity Available
            </label>
            <select
              value={filters.quantity}
              onChange={(e) => handleFilterChange('quantity', e.target.value)}
              className="block w-full px-3 py-2 border border-border rounded-md text-sm
                       focus:border-border-focus focus:ring-1 focus:ring-primary-500 
                       transition-all duration-200 ease-out"
            >
              <option value="">Any Amount</option>
              <option value="small">1-10 lbs</option>
              <option value="medium">10-50 lbs</option>
              <option value="large">50-200 lbs</option>
              <option value="bulk">200+ lbs</option>
            </select>
          </div>

          {/* Harvest Season */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Harvest Season
            </label>
            <select
              value={filters.season}
              onChange={(e) => handleFilterChange('season', e.target.value)}
              className="block w-full px-3 py-2 border border-border rounded-md text-sm
                       focus:border-border-focus focus:ring-1 focus:ring-primary-500 
                       transition-all duration-200 ease-out"
            >
              <option value="">Any Season</option>
              <option value="spring">Spring</option>
              <option value="summer">Summer</option>
              <option value="fall">Fall</option>
              <option value="winter">Winter</option>
            </select>
          </div>

          {/* Special Features */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-3">
              Special Features
            </label>
            <div className="space-y-2">
              {[
                { id: 'pesticide-free', label: 'Pesticide Free' },
                { id: 'local-grown', label: 'Locally Grown' },
                { id: 'fresh-harvest', label: 'Fresh Harvest' },
                { id: 'family-farm', label: 'Family Farm' },
                { id: 'sustainable', label: 'Sustainable Practices' }
              ].map((feature) => (
                <label key={feature.id} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-border text-primary focus:ring-primary-500 
                             focus:ring-1 transition-all duration-200 ease-out"
                  />
                  <span className="text-sm text-text-primary">{feature.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-background">
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-secondary">
              Active filters: {Object.values(filters).filter(value => value && value !== '').length}
            </span>
            <button
              onClick={onClose}
              className="lg:hidden bg-primary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700 transition-all duration-200 ease-out"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterPanel;